import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Container, Spinner, Alert } from "react-bootstrap";
import { useCurrency } from "../../../../context/currencyContext";
import axios from "axios";
import 'bootstrap/dist/css/bootstrap.min.css';
import windowManager from "../../../../services/windowManager";

const ReprintStatement = () => {
  const [account, setAccount] = useState("all");
  const [statementDate, setStatementDate] = useState("");
  const [dontPrintEbilling, setDontPrintEbilling] = useState(false);
  const [dontPrintZeroBalance, setDontPrintZeroBalance] = useState(false);
  const [runDates, setRunDates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [labAccountsOnly, setLabAccountsOnly] = useState([]);
  const { formatAmount } = useCurrency();

  // Read userLabId
  const userLabId = localStorage.getItem("userLabId");

  const sortAccountsAlphabetically = (accounts) => {
    return [...accounts].sort((a, b) => {
      const nameA = a.account_name || "";
      const nameB = b.account_name || "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
  };

  useEffect(() => {
    console.log("🔍 userLabId from localStorage:", userLabId);
    fetchStoredStatementsData();
  }, []);

  const formatDisplayDate = (dateStr) => {
    try {
      const dateObj = new Date(dateStr);
      if (isNaN(dateObj.getTime())) return 'Invalid Date';
      const day = dateObj.getDate();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchStoredStatementsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");

      // === If userLabId is missing ===
      if (!userLabId || userLabId.trim() === "") {
        console.warn("⚠️ userLabId is missing or empty in localStorage");
        setError("Lab ID not found. Please login again or contact support.");
        setAccounts([{ id: "all", account_name: "All Accounts", lastStatementDate: null }]);
        setLabAccountsOnly([]);
        setRunDates([]);
        setLoading(false);
        setInitialLoad(false);
        return;
      }

      // Step 1: Fetch run dates
      const reprintResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/statements/reprint-data', {
        headers: { Authorization: `Bearer ${token}` }
      });

      let formattedRunDates = [];
      if (reprintResponse.data?.success) {
        formattedRunDates = reprintResponse.data.data.runDates.map(date => {
          const displayDate = formatDisplayDate(date.dateValue + 'T00:00:00');
          return { value: date.dateValue, display: displayDate };
        });
      }
      setRunDates(formattedRunDates);

      // Step 2: Fetch all accounts and filter by userLabId
      // First, get all accounts for the lab
      const labResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let accountData = [];
      if (labResponse.data?.success) {
        accountData = labResponse.data.data || [];
        
        // CRITICAL: Filter accounts to ONLY those that belong to this lab
        // This ensures no accounts from other labs appear
        const filteredAccountData = accountData.filter(acc => {
          // Check if the account is associated with this lab
          const belongsToLab = (
            acc.addedBy === userLabId ||
            acc.labId === userLabId ||
            acc.lab_id === userLabId ||
            acc.createdBy === userLabId ||
            (acc.userLabId && acc.userLabId === userLabId)
          );
          
          if (!belongsToLab) {
            console.warn(`⚠️ Filtering out account: ${acc.account_name} (does not belong to lab ${userLabId})`);
          }
          
          return belongsToLab;
        });
        
        console.log(`📊 Total accounts from API: ${accountData.length}, Filtered for lab ${userLabId}: ${filteredAccountData.length}`);
        accountData = filteredAccountData;
      }

      const sortedAccounts = sortAccountsAlphabetically(accountData);
      
      // Store the lab-specific accounts separately for reference
      setLabAccountsOnly(sortedAccounts);
      
      const formattedAccounts = [
        { id: "all", account_name: "All Accounts", lastStatementDate: null },
        ...sortedAccounts.map(acc => ({
          // Use accountId instead of _id for the value that will be passed in URL
          id: acc.accountId ? acc.accountId.toString() : (acc._id ? acc._id.toString() : ""),
          account_name: acc.account_name || "Unknown Account",
          lastStatementDate: acc.lastStatementDate || null,
          // Keep original data for reference
          _id: acc._id,
          addedBy: acc.addedBy,
          labId: acc.labId,
          accountId: acc.accountId
        }))
      ];

      setAccounts(formattedAccounts);

      if (formattedRunDates.length > 0 && initialLoad) {
        setStatementDate(formattedRunDates[0].value);
        setInitialLoad(false);
      }
      
      // If no accounts found for this lab, show appropriate message
      if (sortedAccounts.length === 0) {
        setError(`No accounts found for your lab. Please contact administrator.`);
      }
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError(`Failed to load statement data: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
      if (initialLoad) setInitialLoad(false);
    }
  };

  const handleAccountChange = (e) => {
    const selectedAccountId = e.target.value;
    setAccount(selectedAccountId);
    
    if (selectedAccountId !== "all") {
      const selectedAccount = accounts.find(acc =>
        acc.id === selectedAccountId
      );
      if (selectedAccount?.lastStatementDate) {
        setStatementDate(selectedAccount.lastStatementDate);
      }
    } else if (runDates.length > 0) {
      setStatementDate(runDates[0].value);
    }
  };

  const handleDateChange = (e) => {
    setStatementDate(e.target.value);
  };

  const handleReprint = () => {
    if (!statementDate) {
      alert("Please select a statement date");
      return;
    }
    
    // CRITICAL: When "All Accounts" is selected, we need to pass all accountIds
    // that belong to this lab, not just "all"
    let accountIdToPass = "";
    
    if (account === "all") {
      // Get all account IDs from labAccountsOnly (filtered accounts)
      const labAccountIds = labAccountsOnly
        .map(acc => acc.accountId || acc._id)
        .filter(id => id); // Remove any null/undefined values
      
      if (labAccountIds.length === 0) {
        alert("No accounts found for your lab to reprint statements.");
        return;
      }
      
      // Pass all account IDs as a comma-separated string
      accountIdToPass = labAccountIds.join(",");
      
      console.log(`📋 Reprinting for ALL ${labAccountIds.length} accounts in lab ${userLabId}`);
      console.log(`📋 Account IDs: ${accountIdToPass}`);
    } else {
      // For single account, verify it belongs to this lab
      const selectedAccount = accounts.find(acc => acc.id === account);
      const accountBelongsToLab = labAccountsOnly.some(
        labAcc => (labAcc.accountId === account) || (labAcc._id === selectedAccount?._id)
      );
      
      if (!accountBelongsToLab) {
        console.error(`❌ Error: Account ${account} does not belong to lab ${userLabId}`);
        alert("You don't have permission to reprint statements for this account.");
        return;
      }
      
      accountIdToPass = selectedAccount?.id || account;
    }
    
    const params = new URLSearchParams({
      date: statementDate,
      accountIds: accountIdToPass,
      dontPrintEbilling: dontPrintEbilling.toString(),
      dontPrintZeroBalance: dontPrintZeroBalance.toString(),
      reprint: "true",
      labId: userLabId // Pass labId to backend for additional filtering
    });
    
    const previewUrl = `/rereprint-statement?${params.toString()}`;
    console.log(`🔗 Opening URL: ${previewUrl}`);
    windowManager.openStatement(previewUrl);
  };

  const getSelectedDateDisplay = () => {
    const selectedDate = runDates.find(date => date.value === statementDate);
    return selectedDate ? selectedDate.display : formatDisplayDate(statementDate);
  };

  return (
    <Container fluid className="py-4 px-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: "#004aad", fontWeight: "bold", marginBottom: 0 }}>
          Reprint Statements
        </h2>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3">
          {error}
          <Button
            variant="outline-warning"
            size="sm"
            className="ms-3"
            onClick={fetchStoredStatementsData}
          >
            Retry
          </Button>
        </Alert>
      )}

      <div className="border bg-light rounded shadow-sm" style={{ borderColor: "#d3d3d3" }}>
        <div
          className="bg-white border-bottom border-primary py-2 px-3"
          style={{
            borderBottomWidth: "2px",
            borderTopLeftRadius: "5px",
            borderTopRightRadius: "5px",
            color: "#004aad",
            fontWeight: "bold",
            fontSize: "1.2rem",
          }}
        >
          Statement Details
        </div>
        <div className="p-4">
          {loading && (
            <div className="text-center mb-3">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading statement data...</p>
            </div>
          )}

          {/* Account */}
          <Form.Group as={Row} controlId="account" className="mb-3 align-items-center">
            <Form.Label column sm={12} md={4} style={{ fontWeight: "bold" }}>
              Account <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Col sm={12} md={8}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Form.Control
                  as="select"
                  value={account}
                  onChange={handleAccountChange}
                  className="w-100"
                >
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_name}
                    </option>
                  ))}
                </Form.Control>
              )}
              {!loading && accounts.length <= 1 && (
                <Alert variant="warning" className="mt-2 mb-0 py-2">
                  No accounts available for this lab
                </Alert>
              )}
              {!loading && labAccountsOnly.length > 0 && account === "all" && (
                <div className="mt-2 text-muted small">
                  <i className="bi bi-info-circle"></i> Showing all {labAccountsOnly.length} account(s) for your lab
                </div>
              )}
            </Col>
          </Form.Group>

          {/* Statement Dates */}
          <Form.Group as={Row} controlId="statementDate" className="mb-3 align-items-center">
            <Form.Label column sm={12} md={4} style={{ fontWeight: "bold" }}>
              Statement Dates Available
            </Form.Label>
            <Col sm={12} md={8}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Form.Control
                    as="select"
                    value={statementDate}
                    onChange={handleDateChange}
                    className="w-100"
                    disabled={runDates.length === 0}
                  >
                    {runDates.map((date, i) => (
                      <option key={i} value={date.value}>
                        {date.display}
                      </option>
                    ))}
                  </Form.Control>

                  {!loading && runDates.length === 0 && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                      No statement dates available. No statements have been generated yet.
                    </Alert>
                  )}

                  {!loading && statementDate && runDates.length > 0 && (
                    <div className="mt-2 text-muted small">
                      Selected: {getSelectedDateDisplay()}
                    </div>
                  )}
                </>
              )}
            </Col>
          </Form.Group>

          {/* Checkboxes */}
          <Form.Group as={Row} controlId="dontPrintEbilling" className="mb-3">
            <Col sm={{ span: 12, offset: 0 }} md={{ span: 8, offset: 4 }}>
              <Form.Check
                type="checkbox"
                label={<span style={{ fontWeight: "bold" }}>Don't Print Ebilling Statements</span>}
                checked={dontPrintEbilling}
                onChange={() => setDontPrintEbilling(!dontPrintEbilling)}
                disabled={loading}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="dontPrintZeroBalance" className="mb-3">
            <Col sm={{ span: 12, offset: 0 }} md={{ span: 8, offset: 4 }}>
              <Form.Check
                type="checkbox"
                label={<span style={{ fontWeight: "bold" }}>Don't Print Zero Balance Statements</span>}
                checked={dontPrintZeroBalance}
                onChange={() => setDontPrintZeroBalance(!dontPrintZeroBalance)}
                disabled={loading}
              />
            </Col>
          </Form.Group>

          <div className="mt-4 text-center text-md-start">
            <Button
              variant="primary"
              style={{ backgroundColor: "#004aad", color: "white", borderColor: "#004aad" }}
              onClick={handleReprint}
              disabled={loading || !statementDate || runDates.length === 0 || labAccountsOnly.length === 0}
              size="lg"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                "Preview & Reprint"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ReprintStatement;